import {decodeUriComponentWithTryCatch} from 'coreUtils/url';

import {FIRST_PAGE, GRID_CONFIG, REFINEMENTS_VALUE_SEPARATOR} from 'brandConstants';

import {
	CategoryPageQuery,
	CustomElementIncludeItem,
	GridWidget,
	IProductIncludeCustomElement,
	IOffsetTileWidget,
	IProductsGridWidget,
} from './ICategory';
import AbstractSearchProvider from 'coreClasses/search/providers/AbstractSearchProvider';

const isProductsGridWidget = (widget: GridWidget): widget is IProductsGridWidget => typeof widget.position === 'number';

const isProductInclude = (item: CustomElementIncludeItem): item is IProductIncludeCustomElement =>
	item != null && typeof item === 'object' && item.__typename === 'ProductInclude';

export const parseRefinements = (query: CategoryPageQuery): Record<string, string[]> => {
	const refinements: Record<string, string[]> = {};

	Object.keys(query?.preferences || {}).forEach((keyName) => {
		refinements[keyName] = decodeUriComponentWithTryCatch(query.preferences![keyName]).split(
			REFINEMENTS_VALUE_SEPARATOR,
		);
	});

	Object.keys(query || {}).forEach((keyName) => {
		if (keyName.startsWith('prefn')) {
			const filterKey = query[keyName] as string;
			const filterNumber = keyName.slice(-1);
			const filterValue = query[`prefv${filterNumber}`] as string;

			if (!refinements[filterKey] && filterValue) {
				refinements[filterKey] = decodeUriComponentWithTryCatch(filterValue).split(REFINEMENTS_VALUE_SEPARATOR);
			}
		}
	});

	if (query.cgid) {
		refinements.cgid = [query.cgid];
	}

	return refinements;
};

export const areRefinementsEqual = (a: Record<string, string[]>, b: Record<string, string[]>): boolean => {
	const keysA = Object.keys(a);
	const keysB = Object.keys(b);

	if (keysA.length !== keysB.length) {
		return false;
	}

	return keysA.every((key) => {
		const valA = a[key];
		const valB = b[key];

		if (!valB || valA.length !== valB.length) {
			return false;
		}

		return valA.every((v, i) => v === valB[i]);
	});
};

export const processOffsetTiles = (
	customElementIncludeItems: CustomElementIncludeItem[],
	productsGridWidgetsItems: IProductsGridWidget[],
	pageSize: number,
	pageToLoad: number,
): {customElements: CustomElementIncludeItem[]; gridWidgets: GridWidget[]} | null => {
	if (!customElementIncludeItems?.length || !productsGridWidgetsItems?.length) {
		return null;
	}

	const customElements: CustomElementIncludeItem[] = [...customElementIncludeItems];
	const gridWidgets: GridWidget[] = [...productsGridWidgetsItems];

	const tilesPerPage = pageSize;
	const tilesPerRowDesktop = GRID_CONFIG.DESKTOP.default;
	const tilesPerRowMobile = GRID_CONFIG.MOBILE.default;

	const calculatePageOverflow = () => {
		const pagesDisplacement = new Map<number, {desktop: number; mobile: number}>();

		if (pageToLoad === FIRST_PAGE) {
			return pagesDisplacement;
		}

		let pageNumber = FIRST_PAGE;
		let startPos = pageNumber * tilesPerPage + 1;
		let tilesOccupied = 0;
		let tilesOccupiedMobile = 0;

		const saveDisplacement = () => {
			const desktopDisplacement = tilesOccupied % tilesPerRowDesktop;
			const mobileDisplacement = tilesOccupiedMobile % tilesPerRowMobile;
			pagesDisplacement.set(pageNumber + 1, {
				desktop: desktopDisplacement,
				mobile: mobileDisplacement,
			});
		};

		for (let index = 0; index < gridWidgets.length; index++) {
			const configItem = gridWidgets[index];

			if (!isProductsGridWidget(configItem)) {
				continue;
			}

			if (!customElements[index]) {
				saveDisplacement();

				break;
			}

			if (configItem.position >= startPos) {
				saveDisplacement();

				if (++pageNumber >= pageToLoad) {
					break;
				}

				startPos = pageNumber * tilesPerPage + 1;
				index--;
				continue;
			}

			const width = configItem.width || 1;
			const height = configItem.height || 1;

			tilesOccupied += width * height;
			tilesOccupiedMobile += width === 1 ? height : 0;
		}

		if (pageNumber < pageToLoad) {
			saveDisplacement();
		}

		return pagesDisplacement;
	};

	const pagesDisplacement = calculatePageOverflow();

	if (!pagesDisplacement || pagesDisplacement.size === 0) {
		return {customElements, gridWidgets};
	}

	const pageKeys = Array.from(pagesDisplacement.keys());

	const insertOffsetTile = (pageNumber: number, displacement: {desktop: number; mobile: number}) => {
		const firstTilePos = (pageNumber - 1) * tilesPerPage + 1;

		const elementsBefore = gridWidgets.reduce((acc, config, index) => {
			return (
				acc + (isProductsGridWidget(config) && config.position < firstTilePos && customElements[index] ? 1 : 0)
			);
		}, 0);

		customElements.splice(elementsBefore, 0, null);

		const offsetTile: IOffsetTileWidget = {
			position: `offset-tile-${firstTilePos}`,
			size: displacement.desktop,
			sizeMOB: displacement.mobile,
		};

		gridWidgets.splice(elementsBefore, 0, offsetTile);
	};

	for (let i = pageKeys.length - 1; i >= 0; i--) {
		const currentKey = pageKeys[i];
		const displacement = pagesDisplacement.get(currentKey);
		displacement && insertOffsetTile(currentKey, displacement);
	}

	const lastKey = pageKeys[pageKeys.length - 1];

	if (lastKey && lastKey < pageToLoad) {
		const displacement = pagesDisplacement.get(lastKey);

		if (displacement) {
			for (let pageNumber = pageToLoad; pageNumber > lastKey; pageNumber--) {
				insertOffsetTile(pageNumber, displacement);
			}
		}
	}

	return {customElements, gridWidgets};
};

export const collectStaticProductIds = (customElementIncludeItems?: CustomElementIncludeItem[]): Set<string> => {
	const ids = new Set<string>();

	customElementIncludeItems?.forEach((item) => {
		if (isProductInclude(item)) {
			const productId = item.product?.items?.[0]?.id;

			if (productId) {
				ids.add(productId);
			}
		}
	});

	return ids;
};

export const initSearchManager = (
	searchMgr: AbstractSearchProvider,
	cgid: string | undefined,
	searchQuery: string | undefined,
	srule: string | undefined,
	refinements: Record<string, string[]>,
	pageToLoad: number,
): void => {
	if (searchMgr.getChangedLocale()) {
		searchMgr.setUserSelectedPage(FIRST_PAGE);
	} else {
		searchMgr.setUserSelectedPage(pageToLoad);
	}

	if (cgid) {
		searchMgr.setSearchQuery('');
	}

	searchMgr.setUserSelectedRefinements(refinements);
	searchMgr.enableWildCardQuery(true);
	searchMgr.setChangedLocale(false);
	searchMgr.setUserSelectedSorting(srule);
	searchMgr.setSearchQuery(searchQuery);
};
